const GREEN = "#1a8f68";
const GREEN_LIGHT = "#4ec99a";
const TINT = "#e8f7f1";
const BORDER = "#ede8df";
const BORDER2 = "#e8e2d8";
const PARCHMENT = "#f5f0e8";
const MUTED = "#b0a898";

const card = { background: "#fff", border: `1px solid ${BORDER}`, borderRadius: 14, padding: "12px 14px" };
const eyebrow = { display: "flex", alignItems: "center", gap: 6, fontSize: 9, fontWeight: 800, color: MUTED, letterSpacing: 1.4, textTransform: "uppercase", marginBottom: 10 };

function reviewedToday(wordStates) {
  const startOfDayHours = new Date().setHours(0, 0, 0, 0) / 3600000;
  return Object.values(wordStates || {}).filter(
    (w) => w.lastReviewTime > 0 && w.lastReviewTime >= startOfDayHours
  ).length;
}

function weeklyFromSessions(sessions) {
  const days = [];
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  for (let i = 6; i >= 0; i--) {
    const d = new Date(today);
    d.setDate(today.getDate() - i);
    days.push({ date: d, xp: 0, label: "SMTWTFS"[d.getDay()] });
  }
  (sessions || []).forEach((s) => {
    const t = new Date(s.date ?? s.createdAt ?? s.timestamp ?? 0);
    t.setHours(0, 0, 0, 0);
    const hit = days.find((d) => d.date.getTime() === t.getTime());
    if (hit) hit.xp += s.xpEarned ?? s.xp ?? s.totalAnswers ?? 0;
  });
  let streak = 0;
  for (let i = days.length - 1; i >= 0; i--) {
    if (days[i].xp > 0) streak++;
    else break;
  }
  const maxXp = Math.max(1, ...days.map((d) => d.xp));
  const weekXp = days.reduce((a, d) => a + d.xp, 0);
  return { days, streak, maxXp, weekXp };
}

const IcCheck = () => (
  <svg width="11" height="11" viewBox="0 0 16 16" fill="none" stroke="#fff" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M3 8.5l3.5 3.5L13 5" /></svg>
);
const IcClock = () => (
  <svg width="11" height="11" viewBox="0 0 16 16" fill="none" stroke={GREEN} strokeWidth="1.8" strokeLinecap="round"><circle cx="8" cy="8" r="6.5" /><path d="M8 5v3l2 1.5" /></svg>
);
const IcTrend = () => (
  <svg width="11" height="11" viewBox="0 0 16 16" fill="none" stroke={GREEN} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M2 13l4-4 3 3 5-6" /></svg>
);

function Ring({ value, total }) {
  const pct = total ? Math.min(100, (value / total) * 100) : 0;
  const r = 24, c = 2 * Math.PI * r, off = c * (1 - pct / 100);
  return (
    <div style={{ position: "relative", width: 58, height: 58, flexShrink: 0 }}>
      <svg width="58" height="58" style={{ transform: "rotate(-90deg)" }}>
        <circle cx="29" cy="29" r={r} fill="none" stroke={BORDER} strokeWidth="6" />
        <circle cx="29" cy="29" r={r} fill="none" stroke={GREEN} strokeWidth="6" strokeLinecap="round" strokeDasharray={c} strokeDashoffset={off} style={{ transition: "stroke-dashoffset .6s ease" }} />
      </svg>
      <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
        <b style={{ fontSize: 16, fontWeight: 900, color: "#111", lineHeight: 1 }}>{value}</b>
        <span style={{ fontSize: 8, color: MUTED, fontWeight: 600 }}>of {total}</span>
      </div>
    </div>
  );
}

export default function ProgressRail({ wordStates = {}, sessions = [], dailyGoal = 5 }) {
  const doneToday = Math.min(reviewedToday(wordStates), dailyGoal);
  const { days, streak, maxXp, weekXp } = weeklyFromSessions(sessions);
  const toGo = Math.max(0, dailyGoal - doneToday);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {/* Daily goal */}
      <div style={card}>
        <div style={eyebrow}><IcClock /> Daily Goal</div>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <Ring value={doneToday} total={dailyGoal} />
          <div>
            <b style={{ display: "block", fontSize: 12, fontWeight: 800, color: "#111" }}>
              {toGo === 0 ? "Goal reached! 🎉" : `${toGo} lesson${toGo > 1 ? "s" : ""} to go`}
            </b>
            <span style={{ fontSize: 10, color: MUTED }}>Keep the streak alive.</span>
          </div>
        </div>
      </div>

      {/* Streak week */}
      <div style={card}>
        <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 10 }}>
          <div>
            <b style={{ fontSize: 15, fontWeight: 900, color: "#111" }}>{streak} day{streak === 1 ? "" : "s"}</b>
            <span style={{ fontSize: 10, color: MUTED, display: "block", marginTop: -1 }}>Current streak</span>
          </div>
        </div>
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          {days.map((d, i) => {
            const isToday = i === days.length - 1;
            const on = d.xp > 0;
            return (
              <div key={i} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
                <div style={{
                  width: 22, height: 22, borderRadius: 7, display: "flex", alignItems: "center", justifyContent: "center",
                  background: on ? GREEN : isToday ? TINT : PARCHMENT,
                  border: isToday && !on ? `2px dashed ${GREEN}` : `1.5px solid ${on ? GREEN : BORDER2}`,
                }}>{on ? <IcCheck /> : null}</div>
                <div style={{ fontSize: 9, fontWeight: 700, color: MUTED }}>{d.label}</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Weekly activity */}
      <div style={card}>
        <div style={eyebrow}><IcTrend /> This Week · {weekXp} XP</div>
        <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 5, height: 64 }}>
          {days.map((d, i) => {
            const isToday = i === days.length - 1;
            const h = Math.max(6, (d.xp / maxXp) * 100);
            return (
              <div key={i} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 5, height: "100%", justifyContent: "flex-end" }}>
                <div style={{ width: "100%", maxWidth: 16, height: `${h}%`, borderRadius: "4px 4px 3px 3px", background: isToday ? GREEN : GREEN_LIGHT }} />
                <div style={{ fontSize: 9, fontWeight: 700, color: MUTED }}>{d.label}</div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
